import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { vehicleService } from '../../services/vehicleService';
import { carrierService } from '../../services/carrierService';

const VehicleSchema = Yup.object().shape({
  plateNumber: Yup.string().required('Plaka numarası zorunludur'),
  vehicleTypeId: Yup.number().required('Araç tipi zorunludur'),
  carrierId: Yup.number().required('Taşıyıcı zorunludur'),
  driverName: Yup.string(),
  driverPhone: Yup.string(),
  isActive: Yup.boolean(),
  driverRating: Yup.number().min(0, 'Değerlendirme 0 veya daha büyük olmalıdır').max(5, 'Değerlendirme 5 veya daha küçük olmalıdır')
});

const VehicleEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [vehiclePhoto, setVehiclePhoto] = useState(null);
  const [driverPhoto, setDriverPhoto] = useState(null);

  useEffect(() => {
    fetchVehicle();
    fetchCarriers();
    fetchVehicleTypes();
  }, [id]);

  const fetchVehicle = async () => {
    try {
      const response = await vehicleService.getVehicleById(id);
      if (response && response.success && response.data ) {
        setVehicle(response.data);
      } else {
        throw new Error('Araç bilgileri alınamadı');
      }
    } catch (error) {
      toast.error('Araç bilgileri yüklenirken bir hata oluştu');
      navigate('/vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await carrierService.getCarriers({ isActive: true });
      if (response && response.success && response.data && response.data.rows) {
        setCarriers(response.data.rows);
      }
    } catch (error) {
      toast.error('Taşıyıcılar yüklenirken bir hata oluştu');
      console.error('Error fetching carriers:', error);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response && response.success && response.data) {
        setVehicleTypes(response.data.vehicleTypes);
      }
    } catch (error) {
      toast.error('Araç tipleri yüklenirken bir hata oluştu');
      console.error('Error fetching vehicle types:', error);
    }
  };

  const handleSubmit = async (values) => {
    setIsSaving(true);
    try {
      await vehicleService.updateVehicle(id, values);

      // Araç fotoğrafı yükleme
      if (vehiclePhoto) {
        const formData = new FormData();
        formData.append('vehiclePhoto', vehiclePhoto);
        await vehicleService.uploadVehiclePhoto(id, formData);
      }

      // Sürücü fotoğrafı yükleme
      if (driverPhoto) {
        const formData = new FormData();
        formData.append('driverPhoto', driverPhoto);
        await vehicleService.uploadDriverPhoto(id, formData);
      }

      toast.success('Araç başarıyla güncellendi');
      navigate('/vehicles');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Araç güncellenirken bir hata oluştu');
      console.error('Error updating vehicle:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleVehiclePhotoChange = (event) => {
    setVehiclePhoto(event.currentTarget.files[0]);
  };

  const handleDriverPhotoChange = (event) => {
    setDriverPhoto(event.currentTarget.files[0]);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-500">Araç bulunamadı.</p>
      </div>
    );
  }

  const initialValues = {
    plateNumber: vehicle.plate_number || '',
    vehicleTypeId: vehicle.vehicle_type_id || '',
    carrierId: vehicle.carrier_id || '',
    driverName: vehicle.driver_name || '',
    driverPhone: vehicle.driver_phone || '',
    isActive: vehicle.is_active,
    driverRating: vehicle.driver_rating || 0
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900">Araç Düzenle</h1>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <Formik
          initialValues={initialValues}
          validationSchema={VehicleSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="plateNumber" className="block text-sm font-medium text-gray-700">
                    Plaka Numarası *
                  </label>
                  <div className="mt-1">
                    <Field
                      type="text"
                      name="plateNumber"
                      id="plateNumber"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.plateNumber && touched.plateNumber ? 'border-red-500' : ''
                      }`}
                    />
                    <ErrorMessage name="plateNumber" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="vehicleTypeId" className="block text-sm font-medium text-gray-700">
                    Araç Tipi *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="vehicleTypeId"
                      id="vehicleTypeId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.vehicleTypeId && touched.vehicleTypeId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {vehicleTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name} (Kapasite: {type.capacity})</option>
                      ))}
                    </Field>
                    <ErrorMessage name="vehicleTypeId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="carrierId" className="block text-sm font-medium text-gray-700">
                    Taşıyıcı *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="carrierId"
                      id="carrierId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.carrierId && touched.carrierId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {carriers.map(carrier => (
                        <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                      ))}
                    </Field>
                    <ErrorMessage name="carrierId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="isActive" className="block text-sm font-medium text-gray-700">
                    Durum
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="isActive"
                      id="isActive"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value={true}>Aktif</option>
                      <option value={false}>Pasif</option>
                    </Field>
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="driverName" className="block text-sm font-medium text-gray-700">
                    Sürücü Adı
                  </label>
                  <div className="mt-1">
                    <Field
                      type="text"
                      name="driverName"
                      id="driverName"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="driverPhone" className="block text-sm font-medium text-gray-700">
                    Sürücü Telefonu
                  </label>
                  <div className="mt-1">
                    <Field
                      type="text"
                      name="driverPhone"
                      id="driverPhone"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="driverRating" className="block text-sm font-medium text-gray-700">
                    Sürücü Değerlendirmesi (0-5)
                  </label>
                  <div className="mt-1">
                    <Field
                      type="number"
                      name="driverRating"
                      id="driverRating"
                      min="0"
                      max="5"
                      step="0.1"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.driverRating && touched.driverRating ? 'border-red-500' : ''
                      }`}
                    />
                    <ErrorMessage name="driverRating" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="vehiclePhoto" className="block text-sm font-medium text-gray-700">
                    Araç Fotoğrafı
                  </label>
                  <div className="mt-1">
                    <input
                      type="file"
                      id="vehiclePhoto"
                      name="vehiclePhoto"
                      accept="image/*"
                      onChange={handleVehiclePhotoChange}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  {vehicle.vehicle_photo && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Mevcut fotoğraf: {vehicle.vehicle_photo}</p>
                    </div>
                  )}
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="driverPhoto" className="block text-sm font-medium text-gray-700">
                    Sürücü Fotoğrafı
                  </label>
                  <div className="mt-1">
                    <input
                      type="file"
                      id="driverPhoto"
                      name="driverPhoto"
                      accept="image/*"
                      onChange={handleDriverPhotoChange}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  {vehicle.driver_photo && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Mevcut fotoğraf: {vehicle.driver_photo}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/vehicles')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default VehicleEdit;
